import * as XLSX from 'xlsx';
import type { Truck, Task, Driver } from '@/contexts/DatabaseContext';

function addBOM(csv: string): string {
  return '\uFEFF' + csv;
}

function escapeCsvField(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return '"' + value.replace(/"/g, '""') + '"';
  }
  return value;
}

function downloadBlob(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function downloadXLSX(workbook: XLSX.WorkBook, filename: string): void {
  XLSX.writeFile(workbook, filename, { bookType: 'xlsx', type: 'binary' });
}

// ─── Trucks XLSX export ────────────────────────────

export function exportTrucksToXLSX(trucks: Truck[], t: (key: string) => string): void {
  const headers = [
    t('doorNumber'),
    t('licensePlate'),
    t('generalStatus'),
    t('reportedProblem'),
    t('breakdownDate'),
    t('repairsDone'),
    t('repairStatus'),
    t('comments'),
    t('driver'),
    t('contact'),
  ];

  const rows = trucks.map((truck) => [
    truck.doorNumber,
    truck.licensePlate,
    truck.generalStatus,
    truck.reportedProblem || '',
    truck.breakdownDate
      ? new Date(truck.breakdownDate).toLocaleDateString('fr-FR')
      : '',
    truck.repairsDone || '',
    truck.repairStatus,
    truck.comments || '',
    truck.driverName || '',
    truck.driverContact || '',
  ]);

  const wsData = [headers, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Auto-size columns
  const colWidths = headers.map((h, ci) => {
    const maxLen = Math.max(
      h.length,
      ...rows.map((r) => String(r[ci] || '').length)
    );
    return { wch: Math.min(maxLen + 4, 40) };
  });
  ws['!cols'] = colWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Camions');
  const date = new Date().toISOString().split('T')[0];
  downloadXLSX(wb, `SILY-OBC_Camions_${date}.xlsx`);
}

// ─── Trucks CSV export ─────────────────────────────

export function exportTrucksToCSV(trucks: Truck[], t: (key: string) => string): void {
  const headers = [
    t('doorNumber'),
    t('licensePlate'),
    t('generalStatus'),
    t('reportedProblem'),
    t('breakdownDate'),
    t('repairsDone'),
    t('repairStatus'),
    t('comments'),
    t('driver'),
    t('contact'),
  ];

  const rows = trucks.map((truck) => [
    escapeCsvField(truck.doorNumber),
    escapeCsvField(truck.licensePlate),
    escapeCsvField(truck.generalStatus),
    escapeCsvField(truck.reportedProblem || '-'),
    escapeCsvField(truck.breakdownDate || '-'),
    escapeCsvField(truck.repairsDone || '-'),
    escapeCsvField(truck.repairStatus),
    escapeCsvField(truck.comments || '-'),
    escapeCsvField(truck.driverName || '-'),
    escapeCsvField(truck.driverContact || '-'),
  ]);

  const csv = addBOM([headers.join(','), ...rows.map((r) => r.join(','))].join('\n'));
  const date = new Date().toISOString().split('T')[0];
  downloadBlob(csv, `SILY-OBC_Camions_${date}.csv`, 'text/csv;charset=utf-8');
}

// ─── Trucks PDF export ─────────────────────────────

export function exportTrucksToPDF(trucks: Truck[], t: (key: string) => string): void {
  const headers = [
    t('doorNumber'),
    t('licensePlate'),
    t('generalStatus'),
    t('reportedProblem'),
    t('breakdownDate'),
    t('repairsDone'),
    t('repairStatus'),
    t('driver'),
    t('contact'),
  ];

  const date = new Date().toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const rowsHTML = trucks
    .map(
      (truck) => `
    <tr>
      <td>${truck.doorNumber}</td>
      <td>${truck.licensePlate}</td>
      <td>${truck.generalStatus}</td>
      <td>${truck.reportedProblem || '-'}</td>
      <td>${truck.breakdownDate ? new Date(truck.breakdownDate).toLocaleDateString('fr-FR') : '-'}</td>
      <td>${truck.repairsDone || '-'}</td>
      <td>${truck.repairStatus}</td>
      <td>${truck.driverName || '-'}</td>
      <td>${truck.driverContact || '-'}</td>
    </tr>`
    )
    .join('');

  const headersHTML = headers.map((h) => `<th>${h}</th>`).join('');

  const html = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>SILY-OBC — Rapport Camions</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Inter', 'Segoe UI', sans-serif; padding: 40px; color: #1a1a1a; }
    .header { text-align: center; margin-bottom: 30px; }
    .header h1 { font-size: 22px; font-weight: 700; color: #0d5e3a; margin-bottom: 4px; }
    .header p { font-size: 13px; color: #666; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    thead { background: #f5f5f5; }
    th { padding: 10px 8px; text-align: left; font-weight: 600; border-bottom: 2px solid #ddd; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; color: #555; }
    td { padding: 8px; border-bottom: 1px solid #eee; }
    tr:nth-child(even) { background: #fafafa; }
    .footer { margin-top: 30px; text-align: center; font-size: 11px; color: #999; }
  </style>
</head>
<body>
  <div class="header">
    <h1>SILY-OBC — Rapport des Camions</h1>
    <p>Généré le ${date} — ${trucks.length} camions</p>
  </div>
  <table>
    <thead><tr>${headersHTML}</tr></thead>
    <tbody>${rowsHTML}</tbody>
  </table>
  <div class="footer">&copy; SILY-OBC — Document généré automatiquement</div>
  <script>window.onload = function() { window.print(); }</script>
</body>
</html>`;

  const win = window.open('', '_blank', 'width=1100,height=800');
  if (win) {
    win.document.write(html);
    win.document.close();
  }
}

// ─── Drivers XLSX export ───────────────────────────

export function exportDriversToXLSX(drivers: Driver[], t: (key: string) => string): void {
  const headers = [
    t('driverName'),
    t('driverContact'),
  ];

  const rows = drivers.map((d) => [d.name, d.contact || '']);
  const wsData = [headers, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  const colWidths = headers.map((h, ci) => {
    const maxLen = Math.max(
      h.length,
      ...rows.map((r) => String(r[ci] || '').length)
    );
    return { wch: Math.min(maxLen + 4, 40) };
  });
  ws['!cols'] = colWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Chauffeurs');
  const date = new Date().toISOString().split('T')[0];
  downloadXLSX(wb, `SILY-OBC_Chauffeurs_${date}.xlsx`);
}

// ─── Tasks XLSX export ─────────────────────────────

export function exportTasksToXLSX(tasks: Task[], t: (key: string) => string): void {
  const headers = [
    t('taskTitle'),
    t('taskDescription'),
    t('trucks'),
    t('assignedTo'),
    t('status'),
    t('priority'),
    t('dueDate'),
    t('createdAt'),
  ];

  const rows = tasks.map((task) => [
    task.title,
    task.description,
    task.truckDoorNumber,
    task.assignedToName,
    task.status,
    task.priority,
    task.dueDate || '',
    new Date(task.createdAt).toLocaleDateString('fr-FR'),
  ]);

  const wsData = [headers, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  const colWidths = headers.map((h, ci) => {
    const maxLen = Math.max(
      h.length,
      ...rows.map((r) => String(r[ci] || '').length)
    );
    return { wch: Math.min(maxLen + 4, 40) };
  });
  ws['!cols'] = colWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Tâches');
  const date = new Date().toISOString().split('T')[0];
  downloadXLSX(wb, `SILY-OBC_Taches_${date}.xlsx`);
}

export function exportTasksToCSV(tasks: Task[], t: (key: string) => string): void {
  const headers = [
    t('taskTitle'),
    t('taskDescription'),
    t('trucks'),
    t('assignedTo'),
    t('status'),
    t('priority'),
    t('dueDate'),
    t('createdAt'),
  ];

  const rows = tasks.map((task) => [
    escapeCsvField(task.title),
    escapeCsvField(task.description),
    escapeCsvField(task.truckDoorNumber),
    escapeCsvField(task.assignedToName),
    escapeCsvField(task.status),
    escapeCsvField(task.priority),
    escapeCsvField(task.dueDate || '-'),
    escapeCsvField(new Date(task.createdAt).toLocaleDateString('fr-FR')),
  ]);

  const csv = addBOM([headers.join(','), ...rows.map((r) => r.join(','))].join('\n'));
  const date = new Date().toISOString().split('T')[0];
  downloadBlob(csv, `SILY-OBC_Taches_${date}.csv`, 'text/csv;charset=utf-8');
}

export function exportTasksToPDF(tasks: Task[], t: (key: string) => string): void {
  const headers = [
    t('taskTitle'),
    t('trucks'),
    t('assignedTo'),
    t('status'),
    t('priority'),
    t('dueDate'),
  ];

  const date = new Date().toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const rowsHTML = tasks
    .map(
      (task) => `
    <tr>
      <td>${task.title}</td>
      <td>${task.truckDoorNumber}</td>
      <td>${task.assignedToName}</td>
      <td>${task.status}</td>
      <td><span style="color:${task.priority === 'Haute' ? '#c2410c' : task.priority === 'Moyenne' ? '#b45309' : '#0d5e3a'}">${task.priority}</span></td>
      <td>${task.dueDate ? new Date(task.dueDate).toLocaleDateString('fr-FR') : '-'}</td>
    </tr>`
    )
    .join('');

  const headersHTML = headers.map((h) => `<th>${h}</th>`).join('');

  const html = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>SILY-OBC — Rapport Tâches</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Inter', 'Segoe UI', sans-serif; padding: 40px; color: #1a1a1a; }
    .header { text-align: center; margin-bottom: 30px; }
    .header h1 { font-size: 22px; font-weight: 700; color: #0d5e3a; margin-bottom: 4px; }
    .header p { font-size: 13px; color: #666; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    thead { background: #f5f5f5; }
    th { padding: 10px 8px; text-align: left; font-weight: 600; border-bottom: 2px solid #ddd; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; color: #555; }
    td { padding: 8px; border-bottom: 1px solid #eee; }
    tr:nth-child(even) { background: #fafafa; }
    .footer { margin-top: 30px; text-align: center; font-size: 11px; color: #999; }
  </style>
</head>
<body>
  <div class="header">
    <h1>SILY-OBC — Rapport des Tâches</h1>
    <p>Généré le ${date} — ${tasks.length} tâches</p>
  </div>
  <table>
    <thead><tr>${headersHTML}</tr></thead>
    <tbody>${rowsHTML}</tbody>
  </table>
  <div class="footer">&copy; SILY-OBC — Document généré automatiquement</div>
  <script>window.onload = function() { window.print(); }</script>
</body>
</html>`;

  const win = window.open('', '_blank', 'width=1100,height=800');
  if (win) {
    win.document.write(html);
    win.document.close();
  }
}